/**
 * Shared pure helpers for the Resource Utilization views.
 * Formatting, status mapping, derived aggregates - no React.
 */

export const formatDate = (value) => {
  if (!value) return '-';
  try {
    const d = typeof value === 'string' ? new Date(value) : value;
    if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch (_) {
    return '-';
  }
};

export const formatDateShort = (value) => {
  if (!value) return '-';
  try {
    const d = typeof value === 'string' ? new Date(value) : value;
    if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch (_) {
    return '-';
  }
};

export const daysUntil = (value) => {
  if (!value) return null;
  try {
    const d = typeof value === 'string' ? new Date(value) : value;
    if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;
    const diffMs = d.getTime() - Date.now();
    return Math.round(diffMs / 86400000);
  } catch (_) {
    return null;
  }
};

export const utilizationStatusLabel = (status) => {
  if (status === 'on_track') return 'On track';
  if (status === 'under_target') return 'Under target';
  if (status === 'critical') return 'Critical';
  return 'Unknown';
};

/**
 * Returns Tailwind utility classes for text color based on utilization status.
 */
export const utilizationStatusTextClass = (status) => {
  if (status === 'on_track') return 'text-emerald-600 dark:text-emerald-400';
  if (status === 'under_target') return 'text-amber-600 dark:text-amber-400';
  if (status === 'critical') return 'text-red-600 dark:text-red-400';
  return 'text-primary-700 dark:text-primary-400';
};

/**
 * Returns pill wrapper classes for utilization status.
 */
export const utilizationPillClass = (status) => {
  if (status === 'on_track') {
    return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200';
  }
  if (status === 'under_target') {
    return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200';
  }
  if (status === 'critical') {
    return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200';
  }
  return 'bg-secondary-100 text-secondary-700 dark:bg-secondary-700 dark:text-secondary-200';
};

/**
 * Lifecycle label for an allocation window.
 */
export const lifecycleLabel = (lifecycle) => {
  if (lifecycle === 'active') return 'Active';
  if (lifecycle === 'upcoming') return 'Upcoming';
  if (lifecycle === 'ended') return 'Ended';
  return 'Unknown';
};

export const lifecyclePillClass = (lifecycle) => {
  if (lifecycle === 'active') {
    return 'bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300';
  }
  if (lifecycle === 'upcoming') {
    return 'bg-sky-50 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300';
  }
  if (lifecycle === 'ended') {
    return 'bg-secondary-100 text-secondary-600 dark:bg-secondary-700 dark:text-secondary-200';
  }
  return 'bg-secondary-100 text-secondary-700 dark:bg-secondary-700 dark:text-secondary-200';
};

export const displayName = (user) => {
  if (!user) return '';
  return (
    user.full_name ||
    user.username ||
    user.email ||
    ''
  );
};

/**
 * Billable / non-billable hours inside the active date range. The lifetime
 * `billable_hours` would mark someone billable for old work on another
 * project even when everything in range is non-billable; it is only used
 * as a fallback for payloads that predate the range-aware fields.
 */
export const billableHoursInRange = (user) =>
  Number(user?.billable_week_hours ?? user?.billable_hours ?? 0);

/**
 * Per-resource billability override set in Edit Resource. An empty value
 * means the resource follows each project's billable setting.
 */
export const RESOURCE_BILLABILITY_OPTIONS = [
  { value: '', label: 'Follow project setting' },
  { value: 'billable', label: 'Billable' },
  { value: 'non_billable', label: 'Non-billable' },
];

export const RESOURCE_BILLABILITY_VALUES = ['billable', 'non_billable'];

/** Row-level status: the manual override wins, else billable hours in range. */
export const isResourceBillable = (user) => {
  if (RESOURCE_BILLABILITY_VALUES.includes(user?.billability)) {
    return user.billability === 'billable';
  }
  return billableHoursInRange(user) > 0;
};

export const nonBillableHoursInRange = (user) =>
  Number(user?.non_billable_week_hours ?? user?.non_billable_hours ?? 0);

/**
 * Rolling summary for a users[] list - used by SummaryStats cards.
 */
export const summarizeUsers = (users = []) => {
  let totalEngineers = users.length;
  let withBillable = 0;
  let withNonBillable = 0;
  let shared = 0;
  let overAllocated = 0;
  let freeThisMonth = 0;
  let utilizationSum = 0;
  let utilizationCount = 0;

  const now = new Date();
  const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  users.forEach((u) => {
    if (billableHoursInRange(u) > 0) withBillable += 1;
    if (nonBillableHoursInRange(u) > 0) withNonBillable += 1;
    if (u.is_shared) shared += 1;
    if (u.is_over_allocated) overAllocated += 1;
    if (u.free_date) {
      const fd = new Date(u.free_date);
      if (!Number.isNaN(fd.getTime()) && fd >= now && fd <= monthEnd) {
        freeThisMonth += 1;
      }
    }
    const pct = u.utilization?.percent;
    if (typeof pct === 'number') {
      utilizationSum += pct;
      utilizationCount += 1;
    }
  });

  const avgUtilization = utilizationCount > 0
    ? Math.round(utilizationSum / utilizationCount)
    : 0;

  return {
    totalEngineers,
    withBillable,
    withNonBillable,
    shared,
    overAllocated,
    freeThisMonth,
    avgUtilization,
  };
};

/**
 * Group users by project using their `per_project` entries.
 * Returns [{ project_id, project_name, client, is_billable, members: [{ user, entry }], total_week_hours, total_tracked_hours, planned_capacity }]
 */
export const groupByProject = (users = [], projects = []) => {
  const projectMeta = new Map();
  projects.forEach((p) => {
    if (!p?.id) return;
    projectMeta.set(String(p.id), {
      project_id: String(p.id),
      project_name: p.name,
      client: p.client || '',
      is_billable: Boolean(p.is_billable ?? true),
    });
  });

  const groups = new Map();
  users.forEach((user) => {
    (user.per_project || []).forEach((entry) => {
      const key = String(entry.project_id || '');
      if (!key) return;
      if (!groups.has(key)) {
        const meta = projectMeta.get(key) || {};
        groups.set(key, {
          project_id: key,
          project_name: entry.project_name || meta.project_name || 'Unknown Project',
          client: entry.client ?? meta.client ?? '',
          is_billable: entry.is_billable ?? meta.is_billable ?? true,
          members: [],
          total_week_hours: 0,
          total_tracked_hours: 0,
          planned_capacity: 0,
        });
      }
      const g = groups.get(key);
      g.members.push({ user, entry });
      g.total_week_hours += Number(entry.week_hours || 0);
      g.total_tracked_hours += Number(entry.tracked_hours || 0);
      if (typeof entry.allocation_percent === 'number') {
        g.planned_capacity += entry.allocation_percent;
      }
    });
  });

  const output = Array.from(groups.values()).map((g) => ({
    ...g,
    total_week_hours: Math.round(g.total_week_hours * 10) / 10,
    total_tracked_hours: Math.round(g.total_tracked_hours * 10) / 10,
    planned_capacity: Math.round(g.planned_capacity * 10) / 10,
  }));
  output.sort((a, b) => a.project_name.localeCompare(b.project_name));
  return output;
};
